import { fetchBaseQuery, type BaseQueryFn, type FetchArgs, type FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { Mutex } from 'async-mutex';
import { env } from '@/config/env';
import { oidcService } from '@/services/oidc';
import { clearAuthenticatedUser, updateBreakGlassTokens } from '@/shared/store/slices/authSlice';
import { clearIntendedPath } from '@/shared/store/slices/authIntendedDestinationSlice';
import type { AuthState } from '@/shared/store/slices/authSlice';

const refreshMutex = new Mutex();

const rawBaseQuery = fetchBaseQuery({
  baseUrl: env.VITE_API_URL,
  prepareHeaders: async (headers, { getState }) => {
    const state = getState() as { auth: { accessToken: string | null } };
    const reduxToken = state.auth.accessToken;
    const oidcToken = await oidcService.getAccessToken();
    const token = reduxToken ?? oidcToken;
    if (token) headers.set('Authorization', `Bearer ${token}`);
    // Tenant-scoped endpoints resolve the caller's tenant from this header.
    // Endpoints that target a specific tenant set it explicitly; default to the host tenant otherwise.
    if (!headers.has('X-Tenant-Id')) headers.set('X-Tenant-Id', env.VITE_TENANT_SLUG);
    headers.set('Accept-Language', document.documentElement.lang || 'en');
    return headers;
  },
});

async function renewBreakGlassSession(
  email: string,
  refreshCredential: string,
): Promise<{ accessToken: string; refreshCredential: string } | null> {
  try {
    const response = await fetch(`${env.VITE_API_URL}/auth/break-glass/renew`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Tenant-Id': env.VITE_TENANT_SLUG,
      },
      body: JSON.stringify({ email, refreshCredentialId: refreshCredential }),
    });
    if (!response.ok) return null;
    const data = (await response.json()) as { accessToken: string; refreshCredentialId: string };
    return { accessToken: data.accessToken, refreshCredential: data.refreshCredentialId };
  } catch {
    return null;
  }
}

export const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extra,
) => {
  await refreshMutex.waitForUnlock();

  let result = await rawBaseQuery(args, api, extra);
  if (result.error?.status !== 401) return result;

  if (!refreshMutex.isLocked()) {
    const release = await refreshMutex.acquire();
    try {
      const state = api.getState() as { auth: AuthState };
      const { refreshCredential, user } = state.auth;

      // UC-18: session renewal via POST /auth/break-glass/renew. Every signed-in principal holds a
      // refreshCredential — SSO (UC-16) and break-glass (UC-17) alike (MNT-11) — so this is the
      // single renewal path. There is no OIDC/signinSilent fallback: under the BFF design (UC-16 §2,
      // disablePKCE) the SPA holds no identity-provider session to silently renew.
      if (refreshCredential && user?.email) {
        const renewed = await renewBreakGlassSession(user.email, refreshCredential);
        if (renewed) {
          api.dispatch(updateBreakGlassTokens(renewed));
        } else {
          // Renewal returned 401 (reuse-detection, a revoked/deactivated credential, or any other
          // failure): security-triggered sign-out. RequireAuth reads the cleared slice and redirects.
          api.dispatch(clearAuthenticatedUser());
          api.dispatch(clearIntendedPath());
          return result;
        }
      } else {
        // No renewable credential in the slice — nothing to renew; clear auth so the guard redirects.
        api.dispatch(clearAuthenticatedUser());
        return result;
      }
    } finally {
      release();
    }
  } else {
    await refreshMutex.waitForUnlock();
  }

  result = await rawBaseQuery(args, api, extra);
  if (result.error?.status === 403) {
    api.dispatch(clearAuthenticatedUser());
  }
  return result;
};
